import { Classes } from "@blueprintjs/core";
import { rgba } from "polished";
import React from "react";
import styled, {
  ThemeProvider,
  createGlobalStyle,
  css,
} from "styled-components";

import { popoverStyles } from "../../hex-components/HexPopover";
import BkgNoise from "../../static/image/bkg-noise.png";
import { DARK_THEME } from "../../theme/themes/darkTheme";
import { HexLogo } from "../app/HexLogo";
import { ONBOARDING_MEDIA_QUERY_BREAKPOINT } from "../workspace-setup/common";

const GlobalStyleForPortal = createGlobalStyle`
  .${Classes.POPOVER}.${Classes.POPOVER} {
    ${popoverStyles}
  }
`;

export const VERTICAL_BORDER_PADDING = 15;
export const HORIZONTAL_BORDER_PADDING = 20;

const ContainerWithBackground = styled.div<{ $hideBackgroundBars?: boolean }>`
  position: relative;
  padding: ${HORIZONTAL_BORDER_PADDING}px 0;
  background-color: ${({ theme }) => theme.marketingColors.BACKGROUND};
  background-image: ${({ $hideBackgroundBars, theme }) =>
    $hideBackgroundBars
      ? "none"
      : `linear-gradient(
          to left,
          transparent 145px,
          ${rgba(theme.marketingColors.ROSE_QUARTZ, 0.2)} 145px,
          transparent 146px
        );`};
  background-position: center;
  background-size: 290px 290px;
  color: ${({ theme }) => theme.fontColor.DEFAULT};

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.LARGE}px`}) {
    column-gap: 0;
  }

  &::before {
    content: "";
    display: block;
    pointer-events: none;
    position: fixed;
    top: 0;
    opacity: 0.05;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${BkgNoise});
    background-repeat: repeat;
    background-size: 200px 200px;
  }
`;

const VerticalBorders = styled.div`
  width: calc(100% - ${VERTICAL_BORDER_PADDING * 2}px);
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  margin: 0 ${VERTICAL_BORDER_PADDING}px;
  border-left: 1px solid ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  border-right: 1px solid ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  pointer-events: none;
  opacity: 0.15;
`;

// We want horizontal borders to be on the top and bottom of the page so we need them to be in the page layout to go to the bottom
const HorizontalBorders = styled.div<{ $hideForMobile?: boolean }>`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  pointer-events: none;
  opacity: 0.15;

  ${({ $hideForMobile }) =>
    $hideForMobile &&
    css`
      @media (max-width: ${ONBOARDING_MEDIA_QUERY_BREAKPOINT}px) {
        display: none;
      }
    `}
`;

const HexLogoContainer = styled.div`
  position: absolute;
  top: ${HORIZONTAL_BORDER_PADDING + 1}px;
  left: ${VERTICAL_BORDER_PADDING + 1}px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  background-color: ${({ theme }) => theme.marketingColors.BACKGROUND};

  ${({ theme }) =>
    `
      &::after {
        pointer-events: none;
        width: 100%;
        height: 100%;
        position: absolute;
        content: "";
        opacity: 0.15;
        border-bottom: 1px solid ${theme.marketingColors.ROSE_QUARTZ};
        border-right: 1px solid ${theme.marketingColors.ROSE_QUARTZ};

        @media (max-width: ${theme.mediaQuery.MOBILE}px) {
          border-left: 1px solid ${theme.marketingColors.ROSE_QUARTZ};
        }
      }
      `}

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.MOBILE}px`}) {
    left: calc(50% - 28px);
  }
`;

export const OnboardingContainer = React.memo(function OnboardingContainer({
  children,
  className,
  hideBackgroundBars,
  hideLastHorizontalBarMobile,
  logoProps,
}: {
  children: React.ReactNode;
  className?: string;
  hideBackgroundBars?: boolean;
  logoProps?: React.ComponentProps<typeof HexLogo>;
  hideLastHorizontalBarMobile?: boolean;
}) {
  return (
    <ThemeProvider theme={DARK_THEME}>
      <GlobalStyleForPortal />
      <ContainerWithBackground
        $hideBackgroundBars={hideBackgroundBars}
        className={className}
      >
        <VerticalBorders />
        <HorizontalBorders />
        <HexLogoContainer>
          <HexLogo
            css={css`
              > span > a {
                padding: 10px;
              }
            `}
            forceDarkTheme={true}
            {...logoProps}
          />
        </HexLogoContainer>

        {children}
        <HorizontalBorders $hideForMobile={hideLastHorizontalBarMobile} />
      </ContainerWithBackground>
    </ThemeProvider>
  );
});
